import type { Environment } from "./environment";

export type StorageBackend = "localStorage" | "cookies";

export interface PersistedValueCodec<T> {
  parse(raw: string): T | null;
  serialize(value: T): string;
}

export interface PersistedValueOptions {
  key: string;
  backend?: StorageBackend;
  cookieMaxAgeSeconds?: number;
}

export interface PersistedValue<T> {
  get(): T | null;
  set(value: T): void;
  whenReady: Promise<void>;
}

export function createPersistedValue<T>(
  environment: Environment,
  options: PersistedValueOptions,
  codec: PersistedValueCodec<T>,
): PersistedValue<T> {
  const { key, backend = "localStorage", cookieMaxAgeSeconds } = options;

  const read = async (): Promise<string | null> => {
    try {
      return backend === "cookies"
        ? await environment.cookies.get(key)
        : await environment.localStorage.getItem(key);
    } catch {
      return null;
    }
  };

  const write = async (value: string): Promise<void> => {
    try {
      if (backend === "cookies") {
        await environment.cookies.set(
          key,
          value,
          cookieMaxAgeSeconds != null
            ? { maxAgeSeconds: cookieMaxAgeSeconds }
            : undefined,
        );
      } else {
        await environment.localStorage.setItem(key, value);
      }
    } catch {
      // ignore
    }
  };

  let current: T | null = null;
  const whenReady = read().then((raw) => {
    current = raw === null ? null : codec.parse(raw);
  });

  return {
    get: () => current,
    set(value: T) {
      current = value;
      void write(codec.serialize(value));
    },
    whenReady,
  };
}
